import { createContext, ReactNode, useContext, useState } from "react";
import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";

interface NewestObservation {
    created: string | null;
}

export interface ObservationsModel {
    newestCreated: string | null;
    hasUnseenObservations: boolean;
    markAsSeen: (newestCreated: string) => void;
}

const ObservationsContext = createContext<ObservationsModel>({
    newestCreated: null,
    hasUnseenObservations: false,
    markAsSeen: () => {},
});

export const useObservationsContext = () => {
    return useContext(ObservationsContext);
};

export function hasUnseenObservations(newestCreated: string | null | undefined, lastSeen: string | null) {
    if (!newestCreated) {
        return false;
    }

    return lastSeen === null || new Date(newestCreated) > new Date(lastSeen);
}

// Browser storage is outside the app's control (cleared, blocked, or edited by hand), so anything that is not a date
// reads as never seen: the dot shows once and the next visit writes a valid stamp, instead of the read crashing render
function readLastSeen(storageKey: string) {
    try {
        const stored = localStorage.getItem(storageKey);
        return stored && !isNaN(Date.parse(stored)) ? stored : null;
    } catch {
        return null;
    }
}

function writeLastSeen(storageKey: string, lastSeen: string) {
    try {
        localStorage.setItem(storageKey, lastSeen);
    } catch {
        // Without storage the dot simply shows again on the next load
    }
}

export function ObservationsProvider(props: { children: ReactNode }) {
    const { customerId, permissions } = useUserContext();
    const storageKey = `observationsLastSeen-${customerId}`;
    const [lastSeen, setLastSeenState] = useState(() => readLastSeen(storageKey));

    const setLastSeen = (newestCreated: string) => {
        writeLastSeen(storageKey, newestCreated);
        setLastSeenState(newestCreated);
    };

    // Observations arrive from outside the app (the public report link), so nothing the user does would refetch
    // this. Polling is an async fetch and pauses while the tab is hidden; window focus catches up on return.
    const { data } = useDotLegalQuery<NewestObservation>("/observations/newest", {
        enabled: permissions.observationPermissions.read,
        refetchInterval: 60000,
    });

    // Stamps a server-side Created rather than the browser's clock, so a skewed client clock can neither hide a new
    // observation nor keep the dot lit after the page was opened
    const markAsSeen = (newestCreated: string) => {
        if (newestCreated !== lastSeen) {
            setLastSeen(newestCreated);
        }
    };

    const contextData: ObservationsModel = {
        newestCreated: data?.created ?? null,
        hasUnseenObservations: hasUnseenObservations(data?.created, lastSeen),
        markAsSeen,
    };

    return <ObservationsContext.Provider value={contextData}>{props.children}</ObservationsContext.Provider>;
}

export default ObservationsProvider;
